/**
 * AssessmentPlanSection Component
 *
 * Displays Assessment & Plan information parsed from the scribe JSON.
 * - Renders a list of assessments with ICD-10 code, source, and plan note
 * - Below assessments, renders grouped Details subsections for Referral, Follow-up, and Hand Off
 * - Allows editing and accepting individual assessments; Details subsections are view-only with disabled controls
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Textarea } from './ui/textarea';

type AnyRecord = Record<string, unknown>;

interface AssessmentItem {
  dx: string;
  icd10?: string;
  source?: string;
  planNote?: string;
  type?: string;
  eye?: string;
  severity?: string;
  primary?: string;
  [key: string]: unknown;
}

interface AssessmentFinding {
  finding: string;
  summary: string;
  [key: string]: unknown;
}

interface ApiEnvelope<T = AnyRecord> {
  segmentIds: string[];
  testKey: string;
  data: T[];
}

interface ProcessedAssessmentPlanData {
  assessments: AssessmentItem[];
  assessmentFindings: AssessmentFinding[];
}

interface AssessmentPlanSectionProps {
  jsonContent: AnyRecord | ApiEnvelope[] | ApiEnvelope | null;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: ProcessedAssessmentPlanData) => Promise<void>;
  onAccept?: () => void;
  onAcceptSubsection?: (subKey: string, data: unknown) => void;
  onHighlightSegments?: (segmentIds: string[]) => void;
}

const normalizeString = (value?: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return String(value);
};

const coalesce = (obj: AnyRecord, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim() !== '') {
      return String(value);
    }
  }
  return undefined;
};

// Helper to format plan notes split by |
const formatPlanNote = (planNote: string): string[] => {
  if (!planNote) return [];
  return planNote
    .split('|')
    .map((note) => note.trim())
    .filter((note) => note.length > 0);
};

const AssessmentPlanSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
  onAcceptSubsection,
  onHighlightSegments,
}: AssessmentPlanSectionProps) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<AssessmentItem>({
    dx: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [acceptedSet, setAcceptedSet] = useState<Set<number>>(new Set());
  const [assessmentPlanSegmentIds, setAssessmentPlanSegmentIds] = useState<
    string[]
  >([]);
  const [assessmentFindingSegmentIds, setAssessmentFindingSegmentIds] =
    useState<string[]>([]);

  const processed: ProcessedAssessmentPlanData = useMemo(() => {
    const result: ProcessedAssessmentPlanData = {
      assessments: [],
      assessmentFindings: [],
    };

    if (!jsonContent) return result;

    // If already processed
    if (
      typeof jsonContent === 'object' &&
      !Array.isArray(jsonContent) &&
      ('assessments' in jsonContent || 'assessmentFindings' in jsonContent)
    ) {
      const obj = jsonContent as AnyRecord;
      result.assessments = Array.isArray(obj.assessments)
        ? (obj.assessments as AssessmentItem[])
        : [];
      result.assessmentFindings = Array.isArray(obj.assessmentFindings)
        ? (obj.assessmentFindings as AssessmentFinding[])
        : [];
      return result;
    }

    const pushAssessment = (item: AnyRecord) => {
      const assessment: AssessmentItem = {
        dx:
          coalesce(item, [
            'assessment',
            'assessmentName',
            'diagnosis',
            'dx',
            'name',
          ]) || '',
        icd10:
          coalesce(item, ['icd10Code', 'icd10', 'icdCode', 'dxCode', 'icd']) ||
          undefined,
        source:
          coalesce(item, ['source', 'assessmentSource', 'createdFrom']) ||
          undefined,
        planNote:
          coalesce(item, ['planNote', 'plan', 'note', 'recommendation']) ||
          undefined,
        type: normalizeString(item['type']),
        eye: normalizeString(item['eye']),
        severity: normalizeString(item['severity']),
        primary: normalizeString(item['primary']),
        ...item,
      };
      // Avoid empty placeholder
      if (assessment.dx && assessment.dx.trim() !== '') {
        result.assessments.push(assessment);
      }
    };

    const pushAssessmentFinding = (item: AnyRecord) => {
      const finding: AssessmentFinding = {
        finding: normalizeString(item.finding) || '',
        summary: normalizeString(item.summary) || '',
        ...item,
      };
      // Only add if both finding and summary are present
      if (finding.finding.trim() !== '' && finding.summary.trim() !== '') {
        result.assessmentFindings.push(finding);
      }
    };

    // Array envelope(s) or mixed array with processed objects
    if (Array.isArray(jsonContent)) {
      (jsonContent as Array<ApiEnvelope | AnyRecord>).forEach((entry) => {
        // Case 1: processed-like objects inside the array
        if (
          entry &&
          typeof entry === 'object' &&
          !('testKey' in (entry as AnyRecord)) &&
          ('assessments' in (entry as AnyRecord) ||
            'assessmentFindings' in (entry as AnyRecord))
        ) {
          const processedObj = entry as AnyRecord;
          // Merge assessments
          if (Array.isArray(processedObj.assessments)) {
            (processedObj.assessments as AnyRecord[]).forEach((i) =>
              pushAssessment(i)
            );
          }
          // Merge assessment findings
          if (Array.isArray(processedObj.assessmentFindings)) {
            (processedObj.assessmentFindings as AnyRecord[]).forEach((i) =>
              pushAssessmentFinding(i)
            );
          }
          return;
        }

        // Case 2: API envelope objects
        if (
          entry &&
          typeof entry === 'object' &&
          'testKey' in (entry as AnyRecord) &&
          'data' in (entry as AnyRecord)
        ) {
          const env = entry as ApiEnvelope;
          const tk = env.testKey || '';
          const items = Array.isArray(env.data) ? env.data : [];
          const lowerTk = tk.toLowerCase();

          // Diagnoses from dxplan.diagnosis
          if (lowerTk.startsWith('dxplan') && lowerTk.includes('diagnosis')) {
            items.forEach((i) => pushAssessment(i as AnyRecord));
            setAssessmentPlanSegmentIds(env?.segmentIds || []);
            return;
          }

          // Assessment findings from assessmentFindings.Summary
          if (
            lowerTk.startsWith('assessmentfindings') &&
            lowerTk.includes('summary')
          ) {
            items.forEach((i) => pushAssessmentFinding(i as AnyRecord));
            setAssessmentFindingSegmentIds(env?.segmentIds || []);
            return;
          }
        }
      });
      return result;
    }

    // Single envelope
    if (
      typeof jsonContent === 'object' &&
      'testKey' in (jsonContent as AnyRecord) &&
      'data' in (jsonContent as AnyRecord)
    ) {
      const env = jsonContent as ApiEnvelope;
      const tk = env.testKey || '';
      const items = Array.isArray(env.data) ? env.data : [];
      const lowerTk = tk.toLowerCase();

      if (lowerTk.startsWith('dxplan') && lowerTk.includes('diagnosis')) {
        items.forEach((i) => pushAssessment(i as AnyRecord));
      } else if (
        lowerTk.startsWith('assessmentfindings') &&
        lowerTk.includes('summary')
      ) {
        items.forEach((i) => pushAssessmentFinding(i as AnyRecord));
      }
      return result;
    }

    // Unknown/legacy flat object: try to coerce if recognizable keys exist
    if (typeof jsonContent === 'object') {
      const obj = jsonContent as AnyRecord;
      if (Array.isArray(obj.assessments)) {
        result.assessments = (obj.assessments as AnyRecord[]).map((i) => ({
          dx: normalizeString(coalesce(i, ['dx', 'diagnosis', 'name'])),
          icd10: coalesce(i, ['icd10Code', 'icd10', 'icdCode', 'dxCode']),
          source: coalesce(i, ['source', 'assessmentSource', 'createdFrom']),
          planNote: coalesce(i, ['planNote', 'plan', 'note', 'recommendation']),
          ...(i as AnyRecord),
        }));
      }
      if (Array.isArray(obj.assessmentFindings)) {
        result.assessmentFindings = (obj.assessmentFindings as AnyRecord[]).map(
          (i) => ({
            finding: normalizeString(i.finding),
            summary: normalizeString(i.summary),
            ...(i as AnyRecord),
          })
        );
      }
    }

    return result;
  }, [jsonContent]);

  const handleEditClick = useCallback(
    (i: number) => {
      setEditingIndex(i);
      setEditValues(processed.assessments[i] || { dx: '' });
      setIsEditDialogOpen(true);
    },
    [processed.assessments]
  );

  const handleInputChange = useCallback(
    (field: keyof AssessmentItem, value: string) => {
      setEditValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSaveEdit = useCallback(async () => {
    if (editingIndex == null) return;
    setIsUpdating(true);
    try {
      const updatedAssessments = [...processed.assessments];
      updatedAssessments[editingIndex] = {
        ...updatedAssessments[editingIndex],
        ...editValues,
      };
      await onUpdate({
        assessments: updatedAssessments,
        assessmentFindings: processed.assessmentFindings,
      });
      setIsEditDialogOpen(false);
      setEditingIndex(null);
    } finally {
      setIsUpdating(false);
    }
  }, [
    editingIndex,
    editValues,
    onUpdate,
    processed.assessments,
    processed.assessmentFindings,
  ]);

  const handleCancelEdit = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditingIndex(null);
  }, []);

  const handleAcceptAssessment = useCallback(
    (i: number, item: AssessmentItem) => {
      setAcceptedSet((prev) => new Set(prev).add(i));
      // Send in proper envelope format with testKey and data
      const envelope = [
        {
          testKey: 'dxplan.diagnosis',
          data: [item],
        },
      ];
      if (onAcceptSubsection) onAcceptSubsection(`assessment-${i}`, envelope);
    },
    [onAcceptSubsection]
  );

  const hasAnyAssessments = processed.assessments.length > 0;
  const hasAnyFindings = processed.assessmentFindings.length > 0;

  const handleFindingClick = useCallback(
    (i: number, finding: AssessmentFinding, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onHighlightSegments) return;
      const ids = assessmentFindingSegmentIds;
      if (ids.length > 0) onHighlightSegments(ids);
    },
    [onHighlightSegments]
  );

  const handleDiagnosisClick = useCallback(
    (i: number, item: AssessmentItem, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onHighlightSegments) return;
      const ids = assessmentPlanSegmentIds;
      if (ids.length > 0) onHighlightSegments(ids);
    },
    [onHighlightSegments]
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: (index || 0) * 0.1,
          duration: 0.4,
          ease: 'easeOut',
        }}
        className="group space-y-4">
        {/* Main Card */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden max-w-full">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10 max-w-full overflow-x-hidden">
            {/* Header */}
            <div className="p-4 border-b border-white/20 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide mb-3">
                    Assessment and Plan
                  </h3>
                  {isLoading && (
                    <div className="flex items-center gap-1 ml-2">
                      <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'linear',
                    }}>
                    <Loader2 className="w-4 h-4" />
                  </motion.div>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}>
                    Fetching section content...
                  </motion.span>
                </motion.div>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                  {error && (
                    <div className="text-slate-500 dark:text-slate-400 text-sm italic mt-2">
                      {error === 'Data not found' ? (
                        'No assessment & plan data available yet'
                      ) : (
                        <div className="text-red-600 dark:text-red-400">
                          <span className="font-medium">Error:</span> {error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 pt-0 space-y-6 overflow-x-hidden max-w-full">
              {/* Assessment (Summary of Key Findings) Section */}
              {hasAnyFindings && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                      Assessment (Summary of Key Findings)
                    </h4>
                  </div>
                  <ul>
                    {processed.assessmentFindings.map((finding, i) => (
                      <li
                        key={`finding-${i}`}
                        onClick={(e) => handleFindingClick(i, finding, e)}
                        className={onHighlightSegments ? 'cursor-pointer' : ''}>
                        <div className="flex items-start justify-start gap-1">
                          <div className="w-1.5 h-1.5 bg-gray-800 dark:bg-gray-200 rounded-full mt-3 flex-shrink-0" />
                          <div className="min-w-0 flex-1 ml-1">
                            <p className="text-[16px] text-slate-700 dark:text-slate-300 leading-relaxed break-words">
                              <span className="font-semibold">
                                {finding.finding}:
                              </span>{' '}
                              {finding.summary}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Diagnosis and Plan Section */}
              {hasAnyAssessments && (
                <div
                  className={
                    hasAnyFindings
                      ? 'border-t border-slate-300/50 dark:border-slate-700/30 pt-2'
                      : ''
                  }>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                      Diagnosis and Plan
                    </h4>
                  </div>
                  <div className="space-y-4">
                    {processed.assessments.map((item, i) => {
                      const isAccepted = acceptedSet.has(i);
                      const planNotes = formatPlanNote(item.planNote || '');
                      return (
                        <div
                          key={`diagnosis-${i}`}
                          onClick={(e) => handleDiagnosisClick(i, item, e)}
                          className={
                            onHighlightSegments ? 'cursor-pointer' : ''
                          }>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <h5 className="text-slate-800 dark:text-slate-200 font-semibold text-md tracking-wide break-words break-all whitespace-pre-wrap leading-snug">
                                  {i + 1}. {item.dx || 'Diagnosis'}
                                </h5>
                                {isAccepted && (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                )}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                {item.icd10 && (
                                  <span className="px-2 py-0.5 rounded-xl text-sm bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400 border border-sky-200/30 dark:border-sky-500/30 break-all">
                                    {item.icd10}
                                  </span>
                                )}
                                {item.eye && (
                                  <span className="px-2 py-0.5 rounded-xl text-sm bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400 border border-violet-200/30 dark:border-violet-500/30 break-all">
                                    {item.eye}
                                  </span>
                                )}
                                {item.severity && (
                                  <span className="px-2 py-0.5 rounded-xl text-sm bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200/30 dark:border-amber-500/30 break-all">
                                    {item.severity}
                                  </span>
                                )}
                                {item.primary && item.primary === 'Yes' && (
                                  <span className="px-2 py-0.5 rounded-xl text-sm bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200/30 dark:border-emerald-500/30 break-all">
                                    Primary
                                  </span>
                                )}
                                {item.source && (
                                  <span className="break-words text-sm text-slate-500 dark:text-slate-400">
                                    Source: {item.source}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(i);
                                }}
                                size="sm"
                                className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                                disabled={isLoading}>
                                <Edit3 className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleAcceptAssessment(i, item)}
                                size="sm"
                                className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                                disabled={isLoading}>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Accept
                              </Button>
                            </div>
                          </div>
                          {planNotes.length > 0 && (
                            <div className="ml-4 mt-2">
                              <ul className="space-y-1">
                                {planNotes.map((note, noteIndex) => (
                                  <li
                                    key={`plan-${i}-${noteIndex}`}
                                    className="flex items-start justify-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-gray-800 dark:bg-gray-200 rounded-full mt-3 flex-shrink-0" />
                                    <p className="text-md text-slate-700 dark:text-slate-300 leading-relaxed break-words">
                                      {note}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <div className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Edit Assessment
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-md text-slate-600 dark:text-slate-400">
                Diagnosis
              </Label>
              <Input
                value={editValues.dx || ''}
                onChange={(e) => handleInputChange('dx', e.target.value)}
                placeholder="e.g., Dry Eye Syndrome"
                className="h-9 text-md bg-white/70 dark:bg-slate-800/70 border border-white/30 dark:border-white/15"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-md text-slate-600 dark:text-slate-400">
                  ICD-10
                </Label>
                <Input
                  value={editValues.icd10 || ''}
                  onChange={(e) => handleInputChange('icd10', e.target.value)}
                  placeholder="e.g., H04.123"
                  className="h-9 text-md bg-white/70 dark:bg-slate-800/70 border border-white/30 dark:border-white/15"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-md text-slate-600 dark:text-slate-400">
                  Source
                </Label>
                <Input
                  value={editValues.source || ''}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  placeholder="e.g., AI Generated"
                  className="h-9 text-md bg-white/70 dark:bg-slate-800/70 border border-white/30 dark:border-white/15"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-md text-slate-600 dark:text-slate-400">
                Plan Note
              </Label>

              <Textarea
                value={editValues.planNote || ''}
                onChange={(e) => handleInputChange('planNote', e.target.value)}
                placeholder="Enter plan details"
                className="h-40 text-md bg-white/70 dark:bg-slate-800/70 border border-white/30 dark:border-white/15"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssessmentPlanSection;
